/**
 * Placeholder addresses for users we have no e-mail for.
 *
 * Why they exist: `users.email` is `NOT NULL UNIQUE`, but the Bitrix24
 * Marketplace mandates the MINIMAL user scope, which carries the portal user's
 * id and name and no address at all (moderation item 4, 2026-08-12). Rebuilding
 * a production D1 table to make one column nullable is a far larger risk than
 * storing a value that is unmistakably not a mailbox — so the row gets a
 * placeholder and the code treats it as "no address", never as an address.
 *
 * `.invalid` is reserved by RFC 2606 precisely for this: it is guaranteed never
 * to resolve, so a placeholder that leaks into a send fails at DNS instead of
 * reaching a stranger who happens to own the domain we invented.
 *
 * The placeholder is keyed by our own user id rather than by portal + CRM user
 * id: it is unique by construction (no truncation games with the 64-character
 * local part, no sanitizing of a portal domain), and it carries no CRM data.
 * Identity dedup is NOT this value's job — that is `user_external_ids`.
 *
 * WHAT MUST NEVER HAPPEN: a placeholder reaching an ESP. Every bounce costs
 * sending reputation on `notify.aelo.cloud`, which is shared by all
 * transactional mail. Guard at the point where a recipient is resolved — the
 * skip is worth a log line there, whereas dropping it inside the provider would
 * be a silent failure indistinguishable from a delivery bug.
 */

/** Reserved, never-resolving domain (RFC 2606) used for every placeholder. */
export const PLACEHOLDER_EMAIL_DOMAIN = 'no-mailbox.invalid';

/**
 * The placeholder address for a user we hold no real e-mail for.
 *
 * `userId` is our own UUID, so the result is unique without any further input.
 */
export function placeholderEmailForUser(userId: string): string {
  return `user-${userId}@${PLACEHOLDER_EMAIL_DOMAIN}`;
}

/**
 * Is this stored value a placeholder rather than a mailbox?
 *
 * Recognizes the shape we generate ourselves, so it is an allowlist of one
 * format — not a denylist trying to guess which unknown domains are undeliverable.
 * Any address outside `PLACEHOLDER_EMAIL_DOMAIN` is treated as real, which is
 * the safe direction: the failure mode is "we tried to mail a real person",
 * not "we silently stopped mailing someone".
 */
export function isPlaceholderEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.trim().toLowerCase().endsWith(`@${PLACEHOLDER_EMAIL_DOMAIN}`);
}

/**
 * The address to actually mail, or `null` when there is nobody to mail.
 *
 * Collapses the two "cannot send" cases — absent and placeholder — into one
 * check, so a caller cannot handle the first and forget the second.
 */
export function deliverableEmail(email: string | null | undefined): string | null {
  const trimmed = email?.trim();
  if (!trimmed || isPlaceholderEmail(trimmed)) return null;
  return trimmed;
}
